import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { DataService } from '../../../services/data/data.service';
import { CARD_TYPES, PAYMENT_FLOW_SR_DETAIL, PAYMENT_FLOW_SR_INITIATION, PAYMENT_TYPE_CREDIT_CARD, PAZE } from '../../../utils/constants';
import { BANK_TYPE_FOR_UI, PAYMENT_OPTIONS_UI } from './payment-display.constant';
import { IBankAccountFormValues, ICreditCardFormValues, IPaymentFlow, IPaymentOptionsForUI } from '../../../interfaces/IPayments';
import { IPaymentInfo } from '../../../interfaces/IJob';
import { 
  ACC_TYPE_NEW,
  CARD_TYPE_NEW,
  CARD_TYPE_SAVED,
  PAYMENT_FLOW_CANCEL_PLAN,
  PAYMENT_FLOW_PAST_DUE,
} from '../../../utils/constants';
import { MaskNumberPipe } from '../pipes/mask-number.pipe';
import { PaymentService } from '../../../services/payment/payment.service';
import { IPazeSdk, PazeService } from '../../../services/paze/paze.service';
import { PAZE_ENABLED, PAZE_REPLACEMENT_DEDUCTIBLE_FLAG } from '../../../utils/feature-flags';
import { trackByIndex } from '../../../utils/helper';

@Component({
  selector: 'app-payment-display',
  templateUrl: './payment-display.component.html',
  styleUrl: './payment-display.component.scss'
})
export class PaymentDisplayComponent implements OnInit, OnDestroy {
  @Input() paymentOptionsForm!: FormGroup;
  @Input() savedCardDetails: IPaymentInfo | null = null;
  newCardDetails!: ICreditCardFormValues;
  newBankAccountDetails!: IBankAccountFormValues;
  @Input() editBankAccount: () => void = () => {};
  @Input() editCard: () => void = () => {};
  @Input() callPaze: () => void = () => {};
  cleaner$ = new Subject<void>();
  paymentOptions: IPaymentOptionsForUI[] = structuredClone(PAYMENT_OPTIONS_UI);
  cardTypeSaved: string = CARD_TYPE_SAVED;
  cardTypeNew: string = CARD_TYPE_NEW;
  accTypeNew: string = ACC_TYPE_NEW;
  bankTypeForUI: string = BANK_TYPE_FOR_UI;
  pazeSdk: IPazeSdk|null = null;
  pazePaymentReceived: boolean = false;
  pazePaymentType: string = PAZE;
  pazeReplacementDeductionFlag: boolean = PAZE_REPLACEMENT_DEDUCTIBLE_FLAG;
  trackByIndex = trackByIndex;
  constructor(
    private data: DataService,
    private maskNumberPipe: MaskNumberPipe,
    private paymentService: PaymentService,
    private paze: PazeService
  ) {}

  ngOnInit(): void {
    this.newCardDetails = this.paymentService.initiateCreditCardForm().value;
    this.newBankAccountDetails = this.paymentService.initiateBankAccountForm().value;
    
    this.paymentService.ccForm.valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (formValue: any) => {
        if(!formValue) {return;}
        this.newCardDetails = formValue;
      }
    });
    this.paymentService.bankAccForm.valueChanges.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (bankAccForm: any) => {
        if(!bankAccForm) {return;}
        this.newBankAccountDetails = bankAccForm;
      }
    });

    this.paze.sdkLoader.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (sdk) => this.pazeSdk = sdk
    });

    this.assignPaymentOption(this.data.paymentInitiation$.getValue());

    this.paze.pazePaymentReceived$.pipe(takeUntil(this.cleaner$)).subscribe({
      next: (pazePaymentReceived) => {
        this.pazePaymentReceived = pazePaymentReceived;
      }
    });
    
  }

  ngOnDestroy(): void {
    this.cleaner$.next();
    this.cleaner$.complete();
  }

  assignPaymentOption(flow: IPaymentFlow|null): void {
    switch(flow) {
      case PAYMENT_FLOW_PAST_DUE:
        // remove the saved card option for past due
        this.paymentOptions = this.paymentOptions.filter((option) => ![CARD_TYPE_SAVED, PAZE].includes(option.value));
        // populate the added card details only if it is present
        this.newCardDetails?.cardNumber && this.populateAddedCard(this.newCardDetails);
        // populate the bank account details only if it is present
        if (this.newBankAccountDetails?.accountNumber) {
          this.populateBankAccount(this.newBankAccountDetails);
        } else {
          this.paymentOptions = this.paymentOptions.filter((option) => option.value !== ACC_TYPE_NEW);
        }
        break;
      case PAYMENT_FLOW_SR_INITIATION:
        this.paymentOptions = PAZE_ENABLED && !(this.data.isPlanFFS() && this.data.isPlanFFSSelected$.getValue())
          ? this.filterAccountAndPopulatePazeType()
          : this.paymentOptions.filter((option) => ![ACC_TYPE_NEW, PAZE].includes(option.value));
          
        if (this.data.isVivr$.getValue()) {
          this.paymentOptions = this.paymentOptions.filter((option) => ![ACC_TYPE_NEW, PAZE].includes(option.value));
        }
        // populate the saved card details only if it is present
        this.savedCardDetails?.creditCardTypeCode && this.populateSavedCard(this.savedCardDetails?.creditCardTypeCode);
        if (!this.paymentOptionsForm.controls['paymentOption'].value && this.savedCardDetails?.creditCardTypeCode) {
          // by default card on file will be selected for cancel plan
          this.paymentOptionsForm.controls['paymentOption'].setValue(this.cardTypeSaved);
          this.paymentService.termsAndConditionsToDisplay$.next(PAYMENT_TYPE_CREDIT_CARD);
        }
        // populate the added card details only if it is present
        this.newCardDetails && this.populateAddedCard(this.newCardDetails);
        break;
      case PAYMENT_FLOW_CANCEL_PLAN:
        // remove the bank account option for cancel plan
        this.paymentOptions = this.paymentOptions.filter((option) => ![ACC_TYPE_NEW, PAZE].includes(option.value));
        // populate the saved card details only if it is present
        this.savedCardDetails?.creditCardTypeCode && this.populateSavedCard(this.savedCardDetails?.creditCardTypeCode);
        if (!this.paymentOptionsForm.controls['paymentOption'].value && this.savedCardDetails?.creditCardTypeCode) {
          // by default card on file will be selected for cancel plan
          this.paymentOptionsForm.controls['paymentOption'].setValue(this.cardTypeSaved);
          this.paymentService.termsAndConditionsToDisplay$.next(PAYMENT_TYPE_CREDIT_CARD);
        }
        // populate the added card details only if it is present
        this.newCardDetails && this.populateAddedCard(this.newCardDetails);
        break;
      case PAYMENT_FLOW_SR_DETAIL:
        this.paymentOptions = this.pazeReplacementDeductionFlag ? this.filterAccountAndPopulatePazeType() : this.paymentOptions.filter((option) => ![ACC_TYPE_NEW, PAZE].includes(option.value));
        // populate the saved card details only if it is present
        this.savedCardDetails?.creditCardTypeCode && this.populateSavedCard(this.savedCardDetails?.creditCardTypeCode);
        if (!this.paymentOptionsForm.controls['paymentOption'].value && this.savedCardDetails?.creditCardTypeCode) {
          // by default card on file will be selected for cancel plan
          this.paymentOptionsForm.controls['paymentOption'].setValue(this.cardTypeSaved);
          this.paymentService.termsAndConditionsToDisplay$.next(PAYMENT_TYPE_CREDIT_CARD);
        }
        // populate the added card details only if it is present
        this.newCardDetails && this.populateAddedCard(this.newCardDetails);
        break;
      default:
        // do nothing
        break;
    }
  }

  filterAccountAndPopulatePazeType(): IPaymentOptionsForUI[] {
    // filter out the bank account option
    this.paymentOptions = this.paymentOptions.filter((option) => option.value !== ACC_TYPE_NEW);
    // populate the Paze payment type and return the updated payment options
    return this.paymentOptions.map((option) => (option.value === PAZE) ? { ...option, type: PAZE } : option);
  }

  populateSavedCard(savedCardType: string): void {
    this.paymentOptions.map((option) => {
      if (option.value === this.cardTypeSaved) {
        option.type = savedCardType;
        option.lastFour = this.cardOrAccNumberForUI(this.savedCardDetails?.creditCardNumber || '');
        option.image = CARD_TYPES[savedCardType.toLowerCase()]?.src || '';
      }
      return option;
    });
  }

  populateAddedCard(addedCard: ICreditCardFormValues): void {
    this.paymentOptions.map((option) => {
      if (option.value === this.cardTypeNew) {
        option.type = addedCard.typeCode;
        option.lastFour = this.cardOrAccNumberForUI(addedCard.cardNumber);
        option.image = CARD_TYPES[addedCard.typeCode?.toLowerCase()]?.src || '';
      }
      return option;
    });
  }

  populateBankAccount(bankDetails: IBankAccountFormValues): void {
    this.paymentOptions.map((option) => {
      if (option.value === this.accTypeNew) {
        option.type = BANK_TYPE_FOR_UI;
        option.lastFour = this.cardOrAccNumberForUI(bankDetails.accountNumber);
        option.image = '';
      }
      return option;
    });
  }

  cardOrAccNumberForUI(number: string): string {
    return this.maskNumberPipe.transform(number, 4, '*');
  }
}
